<template>
  <VDialogV3
    id="production-plans-import-dialog"
    class="report-import-dialog content-block-style"
    width="80%"
    height="80%"
    :loading="false"
    not-hide-on-click
    :title="isPlan ? 'Загрузка производственного плана' : 'Загрузка изменений производственного плана'"
    :tabs="isResponseData ? tabsWithCounts : null"
    :active-tab="responsibleDataActiveTab"
    @update:active-tab="setActiveTab"
  >
    <div class="import-wrap">
      <table
        v-if="!isResponseData"
        class="import-wrap-table"
      >
        <thead>
        <tr>
          <th>Поле</th>
          <th>Колонка</th>
          <th>Значение</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(item, index) in importedData"
          :key="index"
          :class="getErrorClass(item)"
        >
          <td>
            {{ item.field }}
          </td>
          <td>
            {{ item.column }}
          </td>
          <td>
            {{ item.value }}
          </td>
        </tr>
        </tbody>
      </table>

      <VDataGridV2
        v-if="isResponseData"
        :key="responsibleDataActiveTab"
        :data="groupedResponseData"
        group-children-property="children"
        sticky
        layout="fill"
      >
        <VDataGridGroupV2>
          <template #default="{ parent }">
            <div
              class="group-header clickable"
              @click="toggleGroup(parent.groupId)"
            >
              <VIcon
                material="chevron_right"
                :class="{ rotated: !parent.collapsed }"
              />
              <b>
                <span class="note">{{ parent.note }}</span>
              </b>
              <b>
                <span class="count">({{ parent.count }})</span>
              </b>
            </div>
          </template>
        </VDataGridGroupV2>


        <VDataGridColumnV2
          id="code"
          label="Код"
          width="140px"
        >
          <template #default="{ row }">
            {{ row.code }}
          </template>
        </VDataGridColumnV2>
        <VDataGridColumnV2
          id="name"
          label="Наименование"
          width="400px"
          content-class="ellipsis"
        >
          <template #default="{ row }">
            {{ row.name }}
          </template>
        </VDataGridColumnV2>
        <VDataGridColumnV2
          v-if="responsibleDataActiveTab.id !== 'updated'"
          id="qty"
          label="Кол-во"
        >
          <template #default="{ row }">
            {{ row.qty }}
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="responsibleDataActiveTab.id === 'updated'"
          id="oldQty"
          label="Старое кол-во"
        >
          <template #default="{ row }">
            {{ row.oldQty }}
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="responsibleDataActiveTab.id === 'updated'"
          id="newQty"
          label="Новое кол-во"
        >
          <template #default="{ row }">
            {{ row.newQty }}
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="status"
          label="Статус"
        >
          <template #default="{ row }">
            {{ row.status }}
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>

    <input
      v-show="!responseData"
      ref="fileInput"
      type="file"
      style="visibility: hidden"
      accept=".xls,.xlsx"
      @change="handleFileUpload($event)"
    >

    <div v-if="responseData && responseData.length > 0 && notFoundProductsCheckResult.length"
         class="products-codes"
    >
      <VIcon material="error"
             theme="outlined"
      />

      <span>{{ notFoundProductsCheckResultText }}</span>
    </div>

    <template #footer>
      <VButton
        theme="primary"
        :disabled="!hasUploadedFile || notFoundProductsCheckResult.length"
        :title="!hasUploadedFile ? 'План не загружен' : ''"
        @click="handlePlan(!isPlan)"
      >
        <template #icon>
          <VIcon :material="isResponseData ? 'cloud_upload' : 'visibility'" />
        </template>
        {{ isResponseData
        ? (isPlan ? "Загрузить план" : "Загрузить изменения")
        : "Посмотреть изменения" }}
      </VButton>

      <VButton
        theme="primary"
        :disabled="!isResponseData"
        :title="!isResponseData ? 'План не загружен' : ''"
        @click="downloadDiff(responseData)"
      >
        <template #icon>
          <VIcon material="download" />
        </template>
        Скачать файл с изменениями
      </VButton>

      <VButton
        theme="primary"
        :disabled="hasUploadedFile"
        @click="triggerFileUpload"
      >
        <template #icon>
          <VIcon material="attach_file" />
        </template>
        Выберите файл
      </VButton>

      <VButton
        theme="default"
        :disabled="!hasUploadedFile"
        :title="!hasUploadedFile ? 'Файл не загружен' : ''"
        @click="clearGrid"
      >
        <template #icon>
          <VIcon
            material="backspace"
            theme="outlined"
          />
        </template>
        Очистить таблицу
      </VButton>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./ProductionPlansImportDialog.scss"/>

<script lang="ts">
import ValidationMixin from '@/mixins/validation-mixin';
import * as XLSX              from 'xlsx';
import ProductionPlansService from '@/domains/ProductionPlans/ProductionPlansService'

export default defineComponent({
  name:   'ProductionPlansImportDialog',

  components: {},

  mixins: [ValidationMixin],

  props: {
    isPlan: {
      type:    Boolean,
      default: false,
    },
  },

  data() {
    return {
      importedData:   [],
      /** данные из файла для отображения в таблице до/для загрузки на сервер */
      responseData:   [],
      /** данные с сервера после загрузки файла на сервер */
      uploadedFile:   null,
      isLoading:      false,
      isResponseData: false,
      /** результат проверки продуктов на существование */
      notFoundProductsCheckResult: 	  [],

      /** меняем таблицу в зависимости от того какие у нас данные отображены */

      visibleSheetName: null,
      isDragging:       false,
      isFileLoading:    false,
      isDragUploading:  false,
      dragCounter:      0,

      /** активная вкладка — строковый id, контракт табов VDialogV3 */
      responsibleDataActiveTab: ProductionPlansService.responseDataTabs[0],
      collapsedGroups: {},
    };
  },

  computed: {
    hasUploadedFile() {
      /** двойное отрицание для преобразования в boolean */
      return !!this.uploadedFile;
    },

    tabsWithCounts() {
      const data = Array.isArray(this.responseData)
        ? this.responseData
        : (this.responseData ? [this.responseData] : []);

      let added = 0, updated = 0, deleted = 0;

      data.forEach(item => {
        const diff = item?.productsDiff || {};
        added   += this.normalizeSection(diff.added).length;
        deleted += this.normalizeSection(diff.deleted).length;

        /** пересчитаем значения чтобы совпадало с кол-во позиций в таблице */

        updated += (diff.updated || []).filter(
          ({ oldValue, newValue }) => oldValue.qty !== newValue.qty
        ).length;
      });

      const all = added + updated + deleted;

      return ProductionPlansService.responseDataTabs.map(tab => ({
        ...tab,
        name: `${tab.name} ${
          tab.id === 'all'    ? `(${all})`
            : tab.id === 'added'  ? `(${added})`
              : tab.id === 'updated'? `(${updated})`
                : tab.id === 'deleted'? `(${deleted})` : ''
        }`
      }));
    },

    notFoundProductsCheckResultText() {
      if (this.notFoundProductsCheckResult.length === 0) {
        return '';
      }
      else {
        return 'Не найдены следующие продукты: \'' + this.notFoundProductsCheckResult.join('\', \'') + '\'';
      }
    },

    /**
     * Сгруппированные данные для VDataGridV2: для каждого плана из responseData
     * собираем плоский список строк по активной вкладке и кладём в children.
     * Если группа схлопнута — children = [], V2 покажет пустую группу.
     */
    groupedResponseData() {
      const tab = this.responsibleDataActiveTab || 'all';

      const makeRowsForItem = item => {
        const diff = item.productsDiff;
        const rows = [];

        this.normalizeSection(diff.added).forEach(product => {
          rows.push({
            name:   product.name,
            code:   product.code,
            qty:    product.qty,
            status: 'добавлено',
          });
        });

        this.normalizeSection(diff.deleted).forEach(product => {
          rows.push({
            name:   product.name,
            code:   product.code,
            qty:    product.qty,
            status: 'удалено',
          });
        });

        /** updated приходит и со старыми и с новыми данными — обрабатываем здесь */
        (diff.updated || []).forEach(({ oldValue, newValue }) => {
          /** не выводим одинаковые значения, чтобы не засорять список */
          if (oldValue.qty === newValue.qty) return;

          rows.push({
            name:   oldValue.name,
            code:   newValue.code,
            oldQty: oldValue.qty,
            newQty: newValue.qty,
            status: 'обновлено',
          });
        });

        /** фильтр по активной вкладке */
        return rows.filter(row => {
          if (tab.id === 'all')     return true;
          if (tab.id === 'updated') return row.status === 'обновлено';
          if (tab.id === 'added')   return row.status === 'добавлено';
          if (tab.id === 'deleted') return row.status === 'удалено';
          return true;
        });
      };

      if (!Array.isArray(this.responseData)) return [];

      return this.responseData
        .map(item => {
          const rows = makeRowsForItem(item);
          if (!rows.length) return null;

          const collapsed = this.isGroupCollapsed(item.planNew.id);

          return {
            id:        item.planNew.id,
            note:      item.planNew.note,
            groupId:   item.planNew.id,
            count:     rows.length,
            collapsed,
            children:  collapsed
              ? []
              : rows.map((row, i) => ({ ...row, id: `${item.planNew.id}-${i}` })),
          };
        })
        .filter(Boolean);
    },
  },

  mounted() {
    window.addEventListener('dragenter', this.handleWindowDragEnter);
    window.addEventListener('dragleave', this.handleWindowDragLeave);
    window.addEventListener('dragover', this.handleWindowDragOver);
    window.addEventListener('drop', this.handleDrop);
  },

  beforeUnmount() {
    window.removeEventListener('dragenter', this.handleWindowDragEnter);
    window.removeEventListener('dragleave', this.handleWindowDragLeave);
    window.removeEventListener('dragover', this.handleWindowDragOver);
    window.removeEventListener('drop', this.handleDrop);
  },

  methods: {

    /** ---------- разбиваем позиции на группы, приводим данные к единому формату на отображение ---------- */

    isGroupCollapsed(id) {
      /** двойное отрицание для преобразования в boolean */
      return !!this.collapsedGroups[id];
    },

    toggleGroup(id) {
      this.collapsedGroups[id] = !this.collapsedGroups[id];
    },

    normalizeSection(section) {

      /** с бека все позиции приходят по-разному,
       * нужно привести все единообразному массиву объектов и выводить */

      if (!section) return [];

      if (Array.isArray(section)) {
        return section.flatMap(item => {
          if (Array.isArray(item)) return item.filter(p => p && typeof p === 'object');
          if (item && typeof item === 'object' && !('code' in item)) {
            return Object.values(item).filter(p => p && typeof p === 'object');
          }
          return [item];
        });
      }

      if (typeof section === 'object') {
        return Object.values(section).filter(p => p && typeof p === 'object');
      }

      return [];
    },

    setActiveTab(tabId) {
      this.responsibleDataActiveTab = tabId;
    },

    /** -------- drag & drop файла ---------- */

    handleWindowDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragCounter++;

      /** показываем overlay только при первом входе, чтобы избежать множественных вызовов при наведении на дочерние элементы */
      if (this.dragCounter === 1) {
        this.isDragging = true;
        this.showDropOverlay();
      }
    },

    handleWindowDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragCounter--;

      /** закрываем overlay только когда мышка вышла за пределы окна (dragCounter === 0) */
      if (this.dragCounter === 0) {
        this.isDragging = false;
        this.hideDropOverlay();
      }
    },

    handleWindowDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
    },

    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = false;
      this.dragCounter = 0;
      this.isDragUploading = true;

      const files = event.dataTransfer.files;
      if (files.length) {
        this.handleFileUpload({ target: { files } });
      }

      /** закрываем overlay после drop */
      this.hideDropOverlay();
    },

    /** ------------------------------------ */

    prepareHeaderString(str) {
      if (typeof str !== 'string') return str;

      /** убираем лишние пробелы внутри строки */
      return str.replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    },

    /** Ищём строку с которой начинается таблица */
    getTableHeaderRow(data) {
      /** итерируемся построчко по документу пользователя */
      for (let i = 0; i < data.length; i++) {
        /** Нормализиуем текущую строку */
        const normalizedHeaderString = data[i].map(item => this.prepareHeaderString(item));

        /** Если в текущей строке найдены все ключевые слова свойственные заголовку, то значит мы нашли заголовок */
        if (ProductionPlansService.requiredFields.every(
          field => normalizedHeaderString.includes(
            this.prepareHeaderString(field)
          )
        )) {
          return i;
        }
      }

      return null;
    },

    showLoadingDialog(title) {
      if (this.loading) return;

      this.isLoading = true;

      this.$core.dialogs.ReportsLoadingDialog.show(
        {
          title,
        },
      );
    },

    closeLoadingDialog() {
      this.isLoading = false;
      this.$core.dialogs.ReportsLoadingDialog.hide();
    },

    clearGrid() {
      this.responseData   = null;
      this.importedData   = null;
      this.uploadedFile   = null;
      this.isResponseData = false;
      this.notFoundProductsCheckResult = [];
      /** сброс инпутов для очистки таблицы */
      this.$refs.fileInput.value = '';
    },
    triggerFileUpload() {
      /** эмитируем upload на VButton */
      this.$refs.fileInput.click();
    },
    getErrorClass(item) {
      /** красный цвет на поля с ошибками */
      return {
        'error-text': item.hasError,
      };
    },
    getVisibleSheetName(workbook) {
      /** Я не знаю почему, но внутри workbook лежит ещё workbook, претензии к автору пакета
       * у них там интерфейс WorkBook с полем workbook, а типы у них ещё интересней,
       * даже разбираться не хочу */
      const visibleSheet = workbook.Workbook.Sheets.find(_sheet => _sheet.Hidden === 0);
      return visibleSheet.name;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];

      if (!file) {
        this.$core.showNotification.warning('Файл не выбран');
        return;
      }

      /** проверка на файл excel */
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      if (!validTypes.includes(file.type)) {
        this.$core.showNotification.error('Выбранный документ не является файлом Excel');
        return;
      }

      /** не консистентно, но выпадает ошибка на blob, пусть будет проверка на этот случай */
      if (!(file instanceof Blob)) {
        this.$core.showNotification.warning('Выбранный файл не является Blob');
        return;
      }

      this.uploadedFile = file;

      const reader = new FileReader();

      reader.readAsArrayBuffer(file);

      reader.onload = e => {
        /** если reader не прочел файл, то будет ошибка */
        const data     = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        /** В документах бывают скрытые листы. Они фигурируют в списке листов,
         * но их нужно игнорировать */
        this.visibleSheetName = this.getVisibleSheetName(workbook);
        const worksheet = workbook.Sheets[this.visibleSheetName];
        const jsonData  = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        /** проверка заголовков и типа файла до заливки в таблицу */
        const headerRowIndex = this.getTableHeaderRow(jsonData);

        if (headerRowIndex === null) {
          /** Выводим ошибку */
          this.printError();
          /** Чистим переменные, в которых лежала таблица */
          this.clearGrid();
          return;
        }

        this.importedData = this.isPlan
          ? this.createProductionPlan(jsonData, headerRowIndex)
          : this.updateProductionPlan(jsonData, headerRowIndex);

        this.$core.showNotification.success('Файл загружен в таблицу');
      };
    },

    /** для обработки производственных планов */
    createProductionPlan(data, headerRowIndex) {
      /** берем вторую строку документа, тк там заголовки */
      const firstRow = data[headerRowIndex].map(item => this.prepareHeaderString(item));

      /** регулярка для вывода для заголовков по логике Заказ Месяц Год */

      const monthRegex    = new RegExp(`заказ\\s(${ProductionPlansService.months.join('|').toLowerCase()})\\s\\d{4}`);

      const extractedData = [];

      ProductionPlansService.requiredFields.forEach(field => {
        const preparedField = this.prepareHeaderString(field);

        /** ищем индекс текущего поля */
        const cellIndex = firstRow.indexOf(preparedField);

        if (cellIndex !== -1) {
          const cell = XLSX.utils.encode_cell({
            c: cellIndex,
            r: 1,
          });
          extractedData.push({
            field:    this.$core.utils.string.capitalize(preparedField),
            cell,
            column:   XLSX.utils.encode_col(cellIndex),
            colIndex: cellIndex,
            value:    firstRow[cellIndex],
            hasError: preparedField !== firstRow[cellIndex],
          });
        } else {
          /** если данных нет, то заменяем объект ошибкой */
          extractedData.push({
            field:    this.$core.utils.string.capitalize(preparedField),
            cell:     'N/A',
            column:   'N/A',
            value:    'Заголовок не найден',
            hasError: true,
          });
        }
      });

      ProductionPlansService.months.forEach(month => {
        extractedData.push({
          field:    month,
          cell:     'N/A',
          column:   'N/A',
          value:    'Поле не найдено',
          hasError: true,
        });
      });

      /** обрабатываем заголовки, содержащие заказы по месяцам и годам */
      firstRow.forEach((header, index) => {
        const preparedHeader = this.prepareHeaderString(header);

        if (typeof preparedHeader === 'string' && monthRegex.test(preparedHeader)) {
          const month      = ProductionPlansService.months.find(m => preparedHeader.includes(this.prepareHeaderString(m)));
          /** Находим месяц в заголовке */
          const hasError   = !month || !monthRegex.test(preparedHeader);
          /** проверяем корректный ли заголовок */
          const cell       = XLSX.utils.encode_cell({
            c: index,
            r: 1,
          });
          const monthField = extractedData.find(item => this.prepareHeaderString(item.field) === this.prepareHeaderString(month) && item.value === 'Поле не найдено');
          if (monthField) {
            monthField.cell     = cell;
            monthField.column   = XLSX.utils.encode_col(index);
            monthField.value    = preparedHeader;
            monthField.colIndex = index;
            monthField.hasError = hasError;
          }
        }
      });

      /** сортируем по индексу столбца */
      extractedData.sort((a, b) => XLSX.utils.decode_col(a.column) - XLSX.utils.decode_col(b.column));

      return extractedData;
    },

    /** форматируем даты excel в даты dd-mm-yyyy */

    convertExcelDateToJSDate(excelDate) {
      const date   = XLSX.SSF.parse_date_code(excelDate);
      const jsDate = new Date(Date.UTC(date.y, date.m - 1, date.d));
      const day    = jsDate.getUTCDate()
        .toString()
        .padStart(2, '0');
      const month  = (jsDate.getUTCMonth() + 1).toString()
        .padStart(2, '0');
      const year   = jsDate.getUTCFullYear();
      return `${day}.${month}.${year}`;
    },

    /** для обработки изменений в планах */
    updateProductionPlan(data, headerRowIndex) {
      /** Чтобы найти строку со статусами, нужно удовостовериться что такая строка вообще есть,
       * если её нет, то печатаем ошибку */
      if (headerRowIndex - 1 < 0) {
        this.printError();
      }

      /** Обычно строка со статусами находится над строкой с наименованием и тд */
      const headerRow     = data[headerRowIndex].map(item => this.prepareHeaderString(item));
      const statusRow     = data[headerRowIndex - 1].map(item => this.prepareHeaderString(item));
      const extractedData = [];

      /** извлекаем аналогично как у загрузки плана */
      ProductionPlansService.requiredFields.forEach(field => {
        const preparedField = this.prepareHeaderString(field);

        const cellIndex = headerRow.indexOf(preparedField);
        if (cellIndex !== -1) {
          extractedData.push({
            field: this.$core.utils.string.capitalize(preparedField),
            cell:  XLSX.utils.encode_cell({
              c: cellIndex,
              r: 3,
            }),
            column:   XLSX.utils.encode_col(cellIndex),
            colIndex: cellIndex,
            value:    headerRow[cellIndex],
            hasError: preparedField !== headerRow[cellIndex],
          });
        } else {
          extractedData.push({
            field:    this.$core.utils.string.capitalize(preparedField),
            cell:     'N/A',
            column:   'N/A',
            colIndex: 'N/A',
            value:    'Заголовок не найден',
            hasError: true,
          });
        }
      });

      /** массив для найденных месяцев */
      const foundMonths = [];

      /** добавляем переменную для динамической смены статуса */
      let currentStatus = 'новый';

      headerRow.forEach((header, index) => {
        const status      = statusRow[index] || '';
        const excelDate   = headerRow[index];
        let formattedDate = null;
        let monthName     = null;

        /** преобразовываем даты excel в дату js */
        if (excelDate && !isNaN(excelDate)) {
          formattedDate   = this.convertExcelDateToJSDate(excelDate);
          const dateParts = formattedDate.split('.');
          /** monthDigits - номера для преобразования даты, из них формируем читаемое monthName */
          monthName = ProductionPlansService.months[ProductionPlansService.monthDigits.indexOf(dateParts[1])];
        }

        /** определяем статус месяца */
        if (status.trim() === 'старый') {
          currentStatus = 'старый';
        } else if (status.trim() === 'новый') {
          currentStatus = 'новый';
        }

        if (formattedDate) {
          /** добавляем данные о статусе в извлеченные данные */
          extractedData.push({
            field: `${monthName} (${currentStatus})`,
            cell:  XLSX.utils.encode_cell({
              c: index,
              r: 4,
            }),
            column:   XLSX.utils.encode_col(index),
            colIndex: index,
            value:    formattedDate,
            excelDate,
            hasError: false,
            isOld:    currentStatus === 'старый',
          });
          foundMonths.push(`${monthName} (${currentStatus})`);
        }
      });

      /** проверка по статусу и добавляем ненайденные месяцы */
      ProductionPlansService.months.forEach(month => {
        if (!foundMonths.includes(`${month} (старый)`)) {
          extractedData.push({
            field:     `${month} (старый)`,
            cell:      'N/A',
            column:    'N/A',
            colIndex:  'N/A',
            value:     'Поле не найдено',
            excelDate: 'N/A',
            hasError:  true,
            isOld:     true,
          });
        }

        if (!foundMonths.includes(`${month} (новый)`)) {
          extractedData.push({
            field:     `${month} (новый)`,
            cell:      'N/A',
            column:    'N/A',
            colIndex:  'N/A',
            value:     'Поле не найдено',
            excelDate: 'N/A',
            hasError:  true,
            isOld:     false,
          });
        }
      });

      extractedData.sort((a, b) => XLSX.utils.decode_col(a.column) - XLSX.utils.decode_col(b.column));

      /** фильтруем данные, убираем старые месяцы */
      return extractedData.filter(item => !item.isOld);
    },

    /** ---- загружаем файл, получаем изменения с сервера, отправляем изменения и заливаем план  ---- */

    /**
     * handlePlan загружает план или изменения на сервер, методы одинаковые,
     * единственная разница это то, что
     * для производственного плана isPlan isAdjustment = false, а для !isPlan true
     * */

    handlePlan(isAdjustment) {
      if (this.isResponseData && this.responseData) {
        this.uploadPlan(this.responseData);
        return;
      }

      const reader  = new FileReader();
      reader.onload = () => {
        const payload = {
          data: this.importedData.map(item => ({
            ...item,
          })),
          file: {
            name: this.uploadedFile.name,
            mimeType: this.uploadedFile.type,
            size: this.uploadedFile.size,
            data: reader.result,
          },
          sheetName: this.visibleSheetName,
          /** required field */
          isAdjustment,
        };

        this.uploadDiff(payload);
      };

      reader.onerror = () => {
        this.$core.showNotification.error('Ошибка при чтении файла');
        // this.closeLoadingDialog();
      };

      reader.readAsDataURL(this.uploadedFile);
    },

    /** получаем дельту с сервера */

    async uploadDiff(payload) {
      this.showLoadingDialog('загрузка изменений');

      try {
        const data = await ProductionPlansService.getDiffs(payload);

        this.responseData   = data;
        this.isResponseData = true;

        const codes = ProductionPlansService.getProductCodesFromDiffs(data);
        await this.checkProductsExist(codes);
        if (data.length > 0) {
          this.$core.showNotification.success(`${this.uploadedFile.name} обработан. Изменения отображены в таблице. `);
        }
      }
      catch (error) {
        this.$core.showNotification.error(`Ошибка при обработке данных: ${error.message}`);
      }
      finally {
        this.closeLoadingDialog();
      }
    },

    async checkProductsExist(products) {
      this.showLoadingDialog('проверка продуктов');
      ProductionPlansService.checkProductsExist({ products })
        .then(data => {
          this.notFoundProductsCheckResult = data;
        })
        .catch(error => {
          this.closeLoadingDialog();
        })
        .finally(() => {
          this.closeLoadingDialog();
        })
    },

    /**
     * uploadPlan достает метод upload c запросом productionPlans.upload,
     * который обрабатывает и загрузку плана и изменений
     * */

    uploadPlan(responseDiff) {
      this.showLoadingDialog('загрузка плана');
      ProductionPlansService.importDiffs({ diffs: responseDiff })
        .then(() => {
          this.$core.showNotification.success(`${this.uploadedFile.name} загружен`);
          /** успех: резолвим true — диалог закрывается, страница обновляет реестр */
          this.$core.dialogs.ProductionPlansImportDialog.resolve(true);
        })
        .catch(error => {
          this.$core.showNotification.error(`Ошибка при загрузке плана: ${error.message}`);
        })
        .finally(() => {
          this.closeLoadingDialog();
        });
    },

    printError() {
      this.$core.showNotification.error(this.isPlan
        ? 'Заголовки не соответствуют плану производства'
        : 'Заголовки не соответствуют изменениям плана производства');
    },

    /** скачать дельту */
    downloadDiff(diff) {
      this.showLoadingDialog('скачивание файла с изменениями');
      ProductionPlansService.exportDiffs({ diffs: diff })
        .then(() => {
          this.$core.showNotification.success('Файл скачан');
        })
        .catch(err => {
          this.$core.showNotification.error(`Ошибка: ${err.message}`);
        })
        .finally(() => {
          this.closeLoadingDialog();
        });
    },

    /** показываем overlay загрузчика файлов */
    showDropOverlay() {
      this.$core.dialogs.ReportsDropOverlayDialog.show(
        {
          isFileLoading: this.isFileLoading && this.isDragUploading
        }
      );
    },

    hideDropOverlay() {
      this.$core.dialogs.ReportsDropOverlayDialog.hide();
    },
  },
});
</script>
